/* ============================================================
   os-wishlist.js — app-free wishlist (localStorage, theme-side).

   Storage:  localStorage key "os:wishlist" — JSON array of product handles.
   Events:   dispatches "wishlist:changed" on document (detail: { handles })
             whenever the saved set changes; dispatches the theme's own
             "cart:refresh" (detail: { open: true }) after a successful
             quick-add so the Be Yours CartDrawer refetches + opens, and
             "ajaxProduct:added" / "ajaxProduct:error" for parity with
             the theme's AddToCart element.
   Contract: card hearts render
             <button data-wishlist-toggle data-product-handle="...">
             This script toggles the class "is-saved" (+ aria-pressed)
             on every [data-wishlist-toggle] whose handle is stored.
   Drawer:   markup lives in snippets/wishlist-drawer.liquid, opened by
             [data-wishlist-open] (header heart) or when a toggle ADDS.
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'os:wishlist';
  var SAVED_CLASS = 'is-saved';

  /* ---------------------------------------------------------- store */
  var store = {
    read: function () {
      try {
        var parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(function (h) { return typeof h === 'string' && h.length > 0; });
      } catch (e) {
        return [];
      }
    },
    write: function (handles) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
      } catch (e) { /* private mode / quota — carry on in-memory */ }
      document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { handles: handles } }));
    },
    has: function (handle) {
      return this.read().indexOf(handle) !== -1;
    },
    add: function (handle) {
      var handles = this.read();
      if (handles.indexOf(handle) === -1) {
        handles.push(handle);
        this.write(handles);
      }
    },
    remove: function (handle) {
      this.write(this.read().filter(function (h) { return h !== handle; }));
    },
    toggle: function (handle) {
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle);
      return true;
    }
  };
  window.osWishlist = store;

  /* ------------------------------------------------------- helpers */
  function rootUrl() {
    var root = (window.theme && theme.routes && theme.routes.root_url) || '/';
    if (root.slice(-1) !== '/') root += '/';
    return root;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function imageUrl(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function formatMoney(cents) {
    var format = (window.theme && theme.shopSettings && theme.shopSettings.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    if (/\{\{\s*amount_no_decimals/.test(format)) {
      amount = String(Math.round(cents / 100));
    } else if (/\{\{\s*amount_with_comma_separator/.test(format)) {
      amount = amount.replace('.', ',');
    }
    // thousands separator on the integer part
    var parts = amount.split(/([.,]\d+)$/);
    amount = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (parts[1] || '');
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  /* --------------------------------------------- product data cache */
  var productCache = {};

  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js')
        .then(function (response) { return response.ok ? response.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  /* ------------------------------------------------ toggles + badge */
  function syncToggles() {
    var handles = store.read();
    var toggles = document.querySelectorAll('[data-wishlist-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      var saved = handles.indexOf(toggles[i].getAttribute('data-product-handle')) !== -1;
      toggles[i].classList.toggle(SAVED_CLASS, saved);
      toggles[i].setAttribute('aria-pressed', saved ? 'true' : 'false');
    }
  }

  function updateBadges() {
    var count = store.read().length;
    var badges = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count > 0 ? String(count) : '';
      badges[i].toggleAttribute('hidden', count === 0);
    }
    var headingCounts = document.querySelectorAll('[data-wishlist-heading-count]');
    for (var j = 0; j < headingCounts.length; j++) {
      headingCounts[j].textContent = count > 0 ? ' (' + count + ')' : '';
    }
  }

  /* ----------------------------------------------------- drawer */
  var drawer = {
    root: null,
    panel: null,
    itemsEl: null,
    emptyEl: null,
    isOpen: false,
    lastFocus: null,
    renderToken: 0,

    init: function () {
      this.root = document.getElementById('os-wishlist-drawer');
      if (!this.root) return;
      this.panel = this.root.querySelector('.os-wishlist__panel');
      this.itemsEl = this.root.querySelector('[data-wishlist-items]');
      this.emptyEl = this.root.querySelector('[data-wishlist-empty]');
      this.onKeydown = this.onKeydown.bind(this);
    },

    open: function () {
      if (!this.root || this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.render();
      this.root.classList.add('is-open');
      document.body.classList.add('os-wishlist-open');
      document.addEventListener('keydown', this.onKeydown);
      var closeButton = this.root.querySelector('[data-wishlist-close]');
      if (closeButton) closeButton.focus();
    },

    close: function () {
      if (!this.root || !this.isOpen) return;
      this.isOpen = false;
      this.root.classList.remove('is-open');
      document.body.classList.remove('os-wishlist-open');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
      this.lastFocus = null;
    },

    onKeydown: function (event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      // minimal focus trap
      var focusables = this.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    setEmpty: function (empty) {
      if (this.emptyEl) this.emptyEl.toggleAttribute('hidden', !empty);
      if (this.itemsEl) this.itemsEl.toggleAttribute('hidden', empty);
    },

    render: function () {
      if (!this.itemsEl) return;
      var self = this;
      var handles = store.read();
      this.setEmpty(handles.length === 0);
      if (!handles.length) {
        this.itemsEl.innerHTML = '';
        return;
      }

      var token = ++this.renderToken;
      Promise.all(handles.map(fetchProduct)).then(function (products) {
        if (token !== self.renderToken) return; // superseded render
        var dead = [];
        var live = [];
        products.forEach(function (product, index) {
          if (product && product.handle) {
            live.push(product);
          } else {
            dead.push(handles[index]);
          }
        });
        // silently drop handles whose product no longer resolves
        if (dead.length) {
          store.write(store.read().filter(function (h) { return dead.indexOf(h) === -1; }));
        }
        self.setEmpty(live.length === 0);
        self.itemsEl.innerHTML = live.map(self.itemHtml).join('');
      });
    },

    itemHtml: function (product) {
      var image = product.featured_image || (product.images && product.images[0]) || '';
      var firstAvailable = null;
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].available) { firstAvailable = product.variants[i]; break; }
      }
      var price = formatMoney(product.price);
      if (product.price_varies) price = 'From ' + price;

      var actionHtml = firstAvailable
        ? '<button type="button" class="os-cta os-cta--solid os-wishlist__add" data-wishlist-add data-variant-id="' + firstAvailable.id + '">Add to cart</button>'
        : '<button type="button" class="os-cta os-cta--solid os-wishlist__add" disabled>Sold out</button>';

      return '' +
        '<li class="os-wishlist__item" data-handle="' + escapeHtml(product.handle) + '">' +
          '<a class="os-wishlist__item-image" href="' + escapeHtml(product.url) + '" tabindex="-1" aria-hidden="true">' +
            (image ? '<img src="' + escapeHtml(imageUrl(image, 160)) + '" alt="" width="80" height="80" loading="lazy">' : '') +
          '</a>' +
          '<div class="os-wishlist__item-info">' +
            '<a class="os-wishlist__item-title" href="' + escapeHtml(product.url) + '">' + escapeHtml(product.title) + '</a>' +
            '<span class="os-wishlist__item-price">' + price + '</span>' +
            '<div class="os-wishlist__item-actions">' + actionHtml + '</div>' +
          '</div>' +
          '<button type="button" class="os-wishlist__item-remove" data-wishlist-remove data-handle="' + escapeHtml(product.handle) + '" aria-label="Remove ' + escapeHtml(product.title) + ' from wishlist">' +
            '<svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.2"/></svg>' +
          '</button>' +
        '</li>';
    }
  };

  /* -------------------------------------------------- add to cart */
  function addToCart(button) {
    var variantId = Number(button.getAttribute('data-variant-id'));
    if (!variantId || button.hasAttribute('disabled')) return;
    button.setAttribute('disabled', '');
    button.classList.add('loading');

    fetch(rootUrl() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (response) { return response.json(); })
      .then(function (parsedState) {
        if (parsedState.status && parsedState.status !== 200) {
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: { errorMessage: parsedState.description || parsedState.message }
          }));
          return;
        }
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
        drawer.close();
        if (document.querySelector('cart-drawer')) {
          // let the wishlist panel start sliding out before the cart drawer slides in;
          // cart:refresh is the theme's own refetch-and-open hook (CartDrawer in global.js)
          setTimeout(function () {
            document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
          }, 250);
        } else {
          window.location.href = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
        }
      })
      .catch(function (error) {
        console.error('Wishlist add-to-cart failed:', error);
      })
      .finally(function () {
        button.classList.remove('loading');
        button.removeAttribute('disabled');
      });
  }

  /* ------------------------------------------------------ wiring */
  function onDocumentClick(event) {
    var toggle = event.target.closest('[data-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      var handle = toggle.getAttribute('data-product-handle');
      if (!handle) return;
      var added = store.toggle(handle);
      if (added) drawer.open();
      return;
    }

    if (event.target.closest('[data-wishlist-open]')) {
      event.preventDefault();
      drawer.open();
      return;
    }

    if (event.target.closest('[data-wishlist-close]') || event.target.closest('.os-wishlist__overlay')) {
      drawer.close();
      return;
    }

    var removeButton = event.target.closest('[data-wishlist-remove]');
    if (removeButton) {
      store.remove(removeButton.getAttribute('data-handle'));
      return;
    }

    var addButton = event.target.closest('[data-wishlist-add]');
    if (addButton) addToCart(addButton);
  }

  function init() {
    drawer.init();
    syncToggles();
    updateBadges();

    document.addEventListener('click', onDocumentClick);

    document.addEventListener('wishlist:changed', function () {
      syncToggles();
      updateBadges();
      if (drawer.isOpen) drawer.render();
    });

    // dynamically appended cards (load-more grids, carousels, section rerenders)
    new MutationObserver(debounce(syncToggles, 150))
      .observe(document.body, { childList: true, subtree: true });

    // cross-tab sync
    window.addEventListener('storage', function (event) {
      if (event.key !== STORAGE_KEY) return;
      syncToggles();
      updateBadges();
      if (drawer.isOpen) drawer.render();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
